// bootstrap.js — injected into every `.html` file in the artifact at publish time:
//
//   <script src="/w/bootstrap.js" data-tinker-token="tok_…" defer></script>
//
// It exists as a separate, tiny file for exactly one reason: it honours the
// **server-controlled kill switch on every boot**, so a bad widget version can be
// pulled without anyone republishing an artifact (`architecture.md` §7.3). If
// `widget.enabled` is false it returns without fetching the widget bundle at all.
//
// It is also the only place the config is fetched. Hosted tags carry an artifact
// token; installed tags carry public installation/build metadata. Neither embeds
// review content, and the widget bundle is handed the config that was already
// fetched rather than asking for it again (contract §1).
//
// Budget: 3kb gzipped. Everything here is on the critical path of a page we do
// not own.

import { BOOTED_FLAG, BOOT_GLOBAL } from "./globals";
import type { BootPayload } from "./globals";
import type { WidgetConfig } from "./contract";
import { activateInstalled, installedContext } from "./installed";

export type BootDeps = {
  win?: Window;
  doc?: Document;
  fetch?: typeof fetch;
};

export async function boot(deps: BootDeps = {}): Promise<void> {
  const win = (deps.win ?? window) as Window & Record<string, unknown>;
  const doc = deps.doc ?? document;

  // An artifact may contain iframes pointing at its own pages, each with the tag
  // injected. One widget per top-level document.
  try {
    if (win.self !== win.top) return;
  } catch {
    return; // cross-origin frame: not our document to instrument
  }

  if (win[BOOTED_FLAG]) return;

  const tag =
    (doc.currentScript as HTMLScriptElement | null) ??
    doc.querySelector<HTMLScriptElement>(
      "script[data-tinker-token], script[data-tinker-installation]",
    );
  const token = tag?.getAttribute("data-tinker-token") ?? "";
  const installed = installedContext(tag);
  if (!token && !installed) return;

  win[BOOTED_FLAG] = true;

  const apiBase =
    installed?.apiBase ??
    (tag?.getAttribute("data-tinker-api") ?? "").replace(/\/$/, "");
  const doFetch = deps.fetch ?? win.fetch?.bind(win) ?? fetch;

  let config: WidgetConfig;
  let authorization: string | null = null;
  let observedBuildRef: string | null = null;

  if (installed) {
    const activation = await activateInstalled(installed, win, doFetch);
    if (activation.state === "rejected") renderRecovery(doc);
    if (activation.state !== "active") return;
    config = activation.config;
    authorization = activation.authorization;
    observedBuildRef = installed.observedBuildRef;
  } else {
    try {
      const response = await doFetch(`${apiBase}/i/config`, {
        credentials: "same-origin",
        cache: "no-store",
      });
      // 401 is `no_session`: fatal. Show nothing, do not retry, do not prompt.
      if (!response.ok) return;
      config = (await response.json()) as WidgetConfig;
    } catch {
      return;
    }
  }

  // The kill switch. Absent counts as off — we never guess our way into running.
  if (!config?.widget?.enabled) return;

  const assetBase = baseOf(tag?.src ?? "");
  const version = encodeURIComponent(config.widget.version ?? "");

  const payload: BootPayload = {
    config,
    apiBase,
    assetBase,
    authorization,
    observedBuildRef,
  };
  win[BOOT_GLOBAL] = payload;

  const script = doc.createElement("script");
  script.src = `${assetBase}widget.js${version ? `?v=${version}` : ""}`;
  script.async = true;
  (doc.head ?? doc.documentElement).append(script);
}

/** The directory the bundles are served from, so `widget.js` sits beside us. */
function baseOf(src: string): string {
  const at = src.lastIndexOf("/");
  return at < 0 ? "/w/" : src.slice(0, at + 1);
}

function renderRecovery(doc: Document): void {
  if (doc.querySelector("tinker-recovery")) return;
  const host = doc.createElement("tinker-recovery");
  const root = host.attachShadow({ mode: "open" });
  root.innerHTML = `<style>:host{position:fixed;right:16px;bottom:16px;z-index:2147483647;max-width:320px;font:14px/1.4 system-ui,sans-serif;color:#211f1c}div{padding:12px 14px;border:1px solid #d8d2c8;border-radius:8px;background:#fff;box-shadow:0 4px 18px #0002}</style><div>This Tinker link can’t start feedback. Reopen the original share link.</div>`;
  (doc.body ?? doc.documentElement).append(host);
}

// Self-guarding: with no injected tag in the document, `boot` returns before it
// touches anything, which is also what makes importing this module in a test inert.
void boot();
